import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import type { TFunction } from 'i18next';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { usePaymentMutations } from '../hooks';

const createPaymentSchema = (t: TFunction) => z.object({
  description: z.string().optional(),
  amount: z.number({ required_error: t('validation.amountRequired') }).min(1, t('validation.amountRequired')),
  paidAt: z.date().optional(),
  billId: z.number({ required_error: t('validation.billRequired') }),
  clientId: z.number().optional(),
  paymentMethodId: z.number().optional(),
});

type CreatePaymentFormData = z.infer<ReturnType<typeof createPaymentSchema>>;

interface CreatePaymentDialogProps {
  billId: number | string;
  currency: string;
  maxAmount?: number;
  /** Controlled open state — when provided, the built-in trigger is hidden. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

export function CreatePaymentDialog({
  billId,
  currency,
  open: openProp,
  onOpenChange,
  hideTrigger,
}: CreatePaymentDialogProps) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (v: boolean) => {
    if (onOpenChange) onOpenChange(v);
    if (!isControlled) setInternalOpen(v);
  };
  const { createMutation } = usePaymentMutations();

  const schema = useMemo(() => createPaymentSchema(t), [t]);

  const form = useForm<CreatePaymentFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      description: '',
      amount: undefined,
      paidAt: undefined,
      billId: Number(billId),
      clientId: undefined,
      paymentMethodId: undefined,
    },
  });

  const onSubmit = (data: CreatePaymentFormData) => {
    const payload = {
      ...data,
      paidAt: data.paidAt?.toISOString(),
    };

    createMutation.mutate(payload, {
      onSuccess: () => {
        setOpen(false);
        form.reset();
        form.setValue('amount', undefined as unknown as number);
      },
    });
  };

  const isSubmitting = createMutation.isPending;

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="hover:bg-green-700 hover:text-white"
          >
            {t('makePayment')}
          </Button>
        </AlertDialogTrigger>
      )}
      <AlertDialogContent className="p-6 max-w-lg">
        <div className="flex items-center justify-between border-b pb-4 border-[#D9D9D9]">
          <AlertDialogTitle className="text-xl font-semibold">
            {t('new')} {t('payment')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button onClick={() => setOpen(false)} aria-label="Close dialog">
              <X size={24} />
            </button>
          </AlertDialogDescription>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 flex flex-col gap-4"
            noValidate
          >
            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('description')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('enterDescription')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Amount */}
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('amount')} {currency === 'USD' ? '$' : 'UZS'} *
                  </FormLabel>
                  <FormControl>
                    <NumericFormat
                      thousandSeparator=" "
                      allowNegative={false}
                      decimalScale={0}
                      customInput={Input}
                      placeholder="123 000"
                      value={field.value ?? ''}
                      onValueChange={(values) =>
                        field.onChange(values.floatValue ?? undefined)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Paid At */}
            <FormField
              control={form.control}
              name="paidAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('paidAt')}</FormLabel>
                  <FormControl>
                    <DateTimePicker
                      value={field.value}
                      onChange={field.onChange}
                      mode="datetime"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Payment Method */}
            <FormField
              control={form.control}
              name="paymentMethodId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('paymentMethod')}</FormLabel>
                  <FormControl>
                    <UniversalCombobox
                      url="/payment-methods"
                      labelKey="name"
                      valueKey="id"
                      selectedId={field.value}
                      onSelectItem={(item) =>
                        field.onChange(item?.['id'] ?? null)
                      }
                      placeholder={t('selectMethod')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Client */}
            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('client')}</FormLabel>
                  <FormControl>
                    <UniversalCombobox
                      url="/clients"
                      labelKey="name"
                      labelKeys={['lastname', 'name', 'middlename']}
                      valueKey="id"
                      selectedId={field.value}
                      onSelectItem={(item) =>
                        field.onChange(item?.['id'] ?? null)
                      }
                      placeholder={t('selectClient')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <AlertDialogFooter className="flex justify-end gap-2 mt-4">
              <AlertDialogCancel onClick={() => setOpen(false)}>
                {t('cancel')}
              </AlertDialogCancel>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isSubmitting ? t('saving') : t('save')}
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
